import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Attachment } from '../../Attachment/attachment.model';

export interface SelectedEmailAttachment extends Core.AppealRecommendationSiteAttachmentModel {
    fileExtension: string;
    fileSize: number;
    file?: File;
}

export interface ArccAttachmentItem extends Attachment {
    selected: boolean;
}

@Injectable()
export class AppealRecommendationTemplateService {
    private _selectedAttachmentsSubject: BehaviorSubject<SelectedEmailAttachment[]> = new BehaviorSubject([]);
    private _selectedAttachments$: Observable<SelectedEmailAttachment[]> =
        this._selectedAttachmentsSubject.asObservable();

    private _files: File[] = [];

    get selectedAttachments$(): Observable<SelectedEmailAttachment[]> {
        return this._selectedAttachments$;
    }

    addFiles(files: File[]): void {
        const current = this._selectedAttachmentsSubject.getValue();

        const newItems = files.map((x) => {
            this._files.push(x);
            const [name, type] = x.name.split('.');
            return {
                attachmentId: `_${this._files.length}`,
                attachmentName: name,
                siteId: null,
                fileExtension: type,
                fileSize: x.size ?? 0,
                file: x,
            } as SelectedEmailAttachment;
        });

        if (newItems.length > 0) {
            this._selectedAttachmentsSubject.next([...current, ...newItems]);
        }
    }

    /**
     * Adds attachments to the selection, skipping any that are already present.
     */
    addAttachments(items: ArccAttachmentItem[]): void {
        const current = this._selectedAttachmentsSubject.getValue();
        const existingIds = new Set(current.map((a) => a.attachmentId));

        const newItems: SelectedEmailAttachment[] = items
            .filter((item) => !existingIds.has(item.attachmentID))
            .map((a) => ({
                attachmentId: a.attachmentID,
                attachmentName: a.fileName,
                fileExtension: a.fileExtension,
                fileSize: a.size ?? 0,
                siteId: a.entityID,
            }));

        if (newItems.length > 0) {
            this._selectedAttachmentsSubject.next([...current, ...newItems]);
        }
    }

    /**
     * Removes a single attachment from the selection by its ID.
     */
    removeSelectedAttachment(attachmentId: System.Guid): boolean {
        const current = this._selectedAttachmentsSubject.getValue();
        const filtered = current.filter((a) => a.attachmentId !== attachmentId);

        if (filtered.length === current.length) {
            return false;
        }

        this._selectedAttachmentsSubject.next(filtered);
        return true;
    }
}
